import { BITRISE_WORKSPACE_PATTERN } from '@hangar/shared';
import type { PlatformProvider, PlatformProviders } from '@/api/schemasPlatformProviders';
import { formatDateTime, timeAgo } from '@/lib/format';

/**
 * How Hangar administration → Expo & Bitrise words Malible's own provider tokens (SPEC decision 19): the status of each,
 * where to get a token, and the form's checks. Pure, so the copy is unit-tested.
 */
export type PlatformProviderKind = PlatformProvider['provider'];
export type TokenField = 'token' | 'workspace';
export type TokenFormValues = { token: string; workspace: string };

export interface ProviderCopy {
  name: string;
  /** The three-letter mark of the card, as Integrations shows providers. */
  mark: string;
  /** What Hangar does with it. */
  purpose: string;
  /** Where to get the token, step by step. */
  where: string[];
  /** The provider's page to start from, opened in a new tab (the deepest page its docs give a URL for). */
  site: { label: string; href: string };
  tokenLabel: string;
  tokenHint: string;
}

export const PROVIDER_COPY: Record<PlatformProviderKind, ProviderCopy> = {
  expo: {
    name: 'Expo',
    mark: 'EAS',
    purpose: 'Creates each client\'s Expo organization, its build robot and the robot\'s token.',
    where: [
      'Sign in at expo.dev as an owner of Malible\'s Expo account.',
      'Open Account settings → Access tokens and create a token.',
      'Use your own (user) token: a robot\'s cannot create organizations.',
    ],
    site: { label: 'Open Expo access tokens', href: 'https://expo.dev/settings/access-tokens' },
    tokenLabel: 'Expo access token',
    tokenHint: 'Checked with Expo before it is saved, then never shown again.',
  },
  bitrise: {
    name: 'Bitrise',
    mark: 'BIT',
    purpose: 'Registers Malible\'s workspace for each client; their Bitrise apps run there.',
    where: [
      'Sign in at bitrise.io with an account of Malible\'s workspace.',
      'Open your profile → Account settings → Security → Personal access tokens and create a token.',
      'Copy the workspace slug from Workspace settings → General settings → Workspace information.',
    ],
    site: { label: 'Open Bitrise', href: 'https://app.bitrise.io' },
    tokenLabel: 'Bitrise personal access token',
    tokenHint: 'Checked with Bitrise against the workspace before it is saved, then never shown again.',
  },
};

export interface StatusView {
  tone: 'succeeded' | 'awaiting';
  label: 'Connected' | 'Not set';
  /** One line under the title: who the token belongs to, or what is missing. */
  detail: string;
}

/** The card's status: connected as an account, or not set (an icon and words, never color alone). */
export function statusView(p: PlatformProvider): StatusView {
  if (!p.configured) return { tone: 'awaiting', label: 'Not set', detail: p.provider === 'expo' ? 'New clients get no Expo organization until it is set' : 'New clients get no Bitrise workspace until it is set' };
  const account = p.account ? `Connected as ${p.account}` : 'Connected (the account could not be confirmed)';
  return { tone: 'succeeded', label: 'Connected', detail: p.workspace && p.account !== p.workspace ? `${account} · ${p.workspace}` : account };
}

/** "Set 2 h ago by jose@malible.com", or null when nothing says when (values from the API's environment). */
export function lastSetText(p: PlatformProvider, now: number = Date.now()): string | null {
  if (!p.configured || !p.updatedAt) return null;
  return `Set ${timeAgo(p.updatedAt, now)}${p.updatedBy ? ` by ${p.updatedBy.label}` : ' outside the console'}`;
}

/** The exact time for the title attribute of `lastSetText`. */
export const lastSetTitle = (p: PlatformProvider): string | undefined => (p.updatedAt ? formatDateTime(p.updatedAt) : undefined);

export const actionLabel = (p: PlatformProvider): 'Set token' | 'Replace token' => (p.configured ? 'Replace token' : 'Set token');

/** When the API reads the tokens from its environment, the page says how to change them instead of offering a form. */
export function sourceNotice(page: Pick<PlatformProviders, 'source' | 'editable'>): string | null {
  if (page.editable) return null;
  return 'This Hangar reads the tokens from the API\'s environment (HANGAR_EXPO_MASTER_TOKEN, HANGAR_BITRISE_TOKEN, HANGAR_BITRISE_WORKSPACE in apps/api/.env), so they cannot be set here: change them there and restart the API, or run it with HANGAR_PLATFORM_SECRETS_PREFIX to keep them in AWS Secrets Manager.';
}

/** The form's own checks (the API checks again, and the provider has the last word). Never echoes the token. */
export function validateTokenForm(kind: PlatformProviderKind, v: TokenFormValues): Partial<Record<TokenField, string>> {
  const errors: Partial<Record<TokenField, string>> = {};
  const token = v.token.trim();
  if (!token) errors.token = `Paste the ${PROVIDER_COPY[kind].tokenLabel.toLowerCase()}`;
  else if (/\s/.test(token)) errors.token = 'A token has no spaces: paste it again';
  else if (token.length < 20) errors.token = 'This looks cut short: paste the whole token';
  if (kind === 'bitrise' && !BITRISE_WORKSPACE_PATTERN.test(v.workspace.trim())) {
    errors.workspace = v.workspace.trim() ? 'Use the slug from the workspace URL (letters, digits, "-" and "_"), not its name' : 'Enter the workspace slug';
  }
  return errors;
}

/** The success toast after a save: the account Hangar now uses. */
export function savedToast(p: PlatformProvider): { title: string; description: string } {
  const name = PROVIDER_COPY[p.provider].name;
  return { title: `${name} token saved`, description: `${p.account ? `Connected as ${p.account}. ` : ''}New clients use it from now on; delete the old token on ${p.provider === 'expo' ? 'expo.dev' : 'bitrise.io'} if you replaced one.` };
}
